import { escapeFilterValue } from '../../lib/pocketbase'
/**
 * Reads run on the portal's own PocketBase client, not the agency singleton.
 *
 * `pb` from lib/pocketbase carries the AGENT's session under a different
 * localStorage key; in an athlete's browser it holds no token at all, and
 * PocketBase answers an unauthorised list with 200 and an empty array. The
 * writes in this file always used getClientPb() — they had to, or they would
 * have failed loudly — so the reads beside them were the half that went
 * quiet. getClientPb() carries the session the row rules are written against.
 */
import { showToast } from '../../components/Toast'
import { getClientUser } from '../clientAuth'
import { getClientPb } from '../../lib/clientPocketbase'
import { isPreview, readOnlyGuard } from './previewMode'
import type {
  ClientContactInfo,
  AgentContactInfo,
  AgencyInfo,
  ClientProfile,
  UpdateClientProfileInput,
} from '../../types/clientPortal'
import type {
  PackageViewSummary,
  AgentPackageActivity,
  PackagePerformanceData,
} from '../../types/playerPackage'

export interface ClientMarketProfile {
  // PVS intentionally excluded — proprietary internal score, not for client view
  performance: PackagePerformanceData | null
  status: string | null
  league: string | null
}

export async function getClientContactInfo(): Promise<ClientContactInfo> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const agency = await getClientPb().collection('agencies').getOne(user.agency_id)

  const agencyInfo: AgencyInfo = {
    id: agency.id,
    // The agencies collection stores these as agency_name / logo_url.
    name: agency.agency_name ?? null,
    logo_url: agency.logo_url || null,
    website: agency.website || null,
    phone: agency.phone || null,
    email: agency.email || null,
    address: agency.address || null,
    city: agency.city || null,
    state: agency.state || null,
    zip: agency.zip || null,
  }

  const agents: AgentContactInfo[] = []
  let primaryAgent: AgentContactInfo | null = null

  try {
    const client = await getClientPb().collection('clients').getOne(user.client_id)
    const leadAgentId = client.lead_agent_id || client.agent_owner_id || client.owner_id

    if (leadAgentId) {
      const leadAgent = await getClientPb().collection('users').getOne(leadAgentId)
      primaryAgent = {
        id: leadAgent.id,
        first_name: leadAgent.first_name ?? null,
        last_name: leadAgent.last_name ?? null,
        full_name: `${leadAgent.first_name ?? ''} ${leadAgent.last_name ?? ''}`.trim() || null,
        email: leadAgent.email ?? null,
        phone: leadAgent.phone || null,
        title: leadAgent.title || leadAgent.role || null,
        avatar: leadAgent.avatar || null,
        is_primary: true,
      }
      agents.push(primaryAgent)
    }

    const { getClientDealAgentIds } = await import('./deals')
    const uniqueAgentIds = new Set<string>()
    for (const ownerId of await getClientDealAgentIds()) {
      if (ownerId !== leadAgentId) uniqueAgentIds.add(ownerId)
    }

    for (const agentId of uniqueAgentIds) {
      try {
        const agent = await getClientPb().collection('users').getOne(agentId)
        agents.push({
          id: agent.id,
          first_name: agent.first_name ?? null,
          last_name: agent.last_name ?? null,
          full_name: `${agent.first_name ?? ''} ${agent.last_name ?? ''}`.trim() || null,
          email: agent.email ?? null,
          phone: agent.phone || null,
          title: agent.title || agent.role || null,
          avatar: agent.avatar || null,
          is_primary: false,
        })
      } catch {
        // Agent was deleted or inaccessible — skip silently
      }
    }
  } catch (err) {
    showToast((err as any).message || 'Failed to load agent details', 'error')
  }

  return {
    agency: agencyInfo,
    agents,
    primary_agent: primaryAgent,
  }
}

export async function getClientProfile(): Promise<ClientProfile> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const clientPb = getClientPb()
  const record = await clientPb.collection('client_users').getOne(user.id, {
    expand: 'client_id,agency_id',
  })

  return {
    id: record.id,
    email: record.email ?? null,
    name: record.name ?? null,
    client_id: record.client_id,
    agency_id: record.agency_id,
    avatar: record.avatar || null,
    last_login: record.last_login || null,
    relationship: (record.relationship || 'athlete') as ClientProfile['relationship'],
    show_contracts: record.show_contracts ?? true,
    show_incentives: record.show_incentives ?? true,
    show_earnings_detail: record.show_earnings_detail ?? false,
    show_commission: record.show_commission === true,
    created: record.created,
    updated: record.updated,
    phone: record.phone || null,
    address: record.address || null,
    city: record.city || null,
    state: record.state || null,
    zip: record.zip || null,
    country: record.country || null,
    preferred_contact: record.preferred_contact || null,
    timezone: record.timezone || null,
    notification_preferences: record.notification_preferences || null,
    emergency_contact_name: record.emergency_contact_name || null,
    emergency_contact_phone: record.emergency_contact_phone || null,
    emergency_contact_relation: record.emergency_contact_relation || null,
    expand: {
      client_id: record.expand?.client_id
        ? {
            id: record.expand.client_id.id,
            full_name: record.expand.client_id.full_name ?? null,
            sport: record.expand.client_id.sport || null,
            position: record.expand.client_id.position || null,
            league: record.expand.client_id.league || null,
            team_name: record.expand.client_id.team_name || null,
            headshot_url: record.expand.client_id.headshot_url || null,
            status: record.expand.client_id.status || null,
            stats_summary: record.expand.client_id.stats_summary || null,
            avatar: record.expand.client_id.avatar || null,
          }
        : undefined,
      agency_id: record.expand?.agency_id
        ? {
            id: record.expand.agency_id.id,
            // Column names are agency_name / logo_url, not name / logo.
            name: record.expand.agency_id.agency_name ?? null,
            logo: record.expand.agency_id.logo_url || null,
            portal_primary_color: record.expand.agency_id.primary_color || null,
          }
        : undefined,
    },
  }
}

export async function updateClientProfile(
  updates: UpdateClientProfileInput
): Promise<ClientProfile> {
  readOnlyGuard('changing their details')
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const clientPb = getClientPb()
  await clientPb.collection('client_users').update(user.id, updates)
  return getClientProfile()
}

export async function getClientMarketProfile(): Promise<ClientMarketProfile> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const client = await getClientPb().collection('clients').getOne(user.client_id)

  // PVS score intentionally NOT loaded for client portal
  // It's a proprietary internal valuation tool — could hurt feelings if score is low

  let performance: PackagePerformanceData | null = null
  if (client.stats_summary) {
    const stats = typeof client.stats_summary === 'string'
      ? JSON.parse(client.stats_summary)
      : client.stats_summary
    const overallGrade = stats.overall_grade || stats.pff_grade || null
    const keyStats: { label: string; value: string }[] = []
    const position = (client.position || '').toUpperCase()

    const statMappings: Record<string, [string, string][]> = {
      QB: [['Pass Yards', 'passing_yards'], ['TDs', 'passing_touchdowns'], ['Rating', 'passer_rating']],
      WR: [['Rec Yards', 'receiving_yards'], ['Receptions', 'receptions'], ['TDs', 'receiving_touchdowns']],
      RB: [['Rush Yards', 'rushing_yards'], ['TDs', 'rushing_touchdowns'], ['YPC', 'yards_per_carry']],
      TE: [['Rec Yards', 'receiving_yards'], ['Receptions', 'receptions'], ['TDs', 'receiving_touchdowns']],
    }
    const mappings = statMappings[position] || []
    for (const [label, key] of mappings) {
      if (stats[key] != null) keyStats.push({ label, value: String(stats[key]) })
    }

    if (overallGrade || keyStats.length > 0) {
      performance = { overall_grade: overallGrade ? Number(overallGrade) : null, key_stats: keyStats, season: stats.season || null }
    }
  }

  return {
    performance,
    status: client.status || null,
    league: client.league || null,
  }
}

export async function getClientPackageViews(): Promise<PackageViewSummary> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const result = await getClientPb().collection('package_views').getList(1, 500, {
    filter: `client_id = "${escapeFilterValue(user.client_id)}"`,
    sort: '-created',
  })

  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  const recentViews = result.items.filter(v => new Date(v.created) > thirtyDaysAgo)
  const uniqueTeams = new Set(result.items.map(v => v.target_team_id).filter(Boolean))

  const recentWithNames: { team_name?: string; date: string }[] = []
  for (const view of result.items.slice(0, 10)) {
    let teamName: string | undefined
    if (view.target_team_id) {
      try {
        const team = await getClientPb().collection('teams').getOne(view.target_team_id)
        teamName = team.name
      } catch (err) { showToast((err as any).message || 'Failed to load team details', 'error') }
    }
    recentWithNames.push({ team_name: teamName, date: view.created })
  }

  return {
    total_views: result.totalItems,
    views_last_30_days: recentViews.length,
    teams_reached: uniqueTeams.size,
    recent_views: recentWithNames,
  }
}

export async function getClientPackageActivity(): Promise<AgentPackageActivity> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const result = await getClientPb().collection('shared_reports').getList(1, 50, {
    filter: `client_id = "${escapeFilterValue(user.client_id)}" && report_type = "player_package"`,
    sort: '-created',
  })

  const teamsContacted: AgentPackageActivity['teams_contacted'] = []
  for (const pkg of result.items) {
    if (pkg.target_team_id) {
      try {
        const team = await getClientPb().collection('teams').getOne(pkg.target_team_id)
        teamsContacted.push({ team_name: team.name, logo_url: team.logo_url || null, date_shared: pkg.created })
      } catch (err) {
        showToast((err as any).message || 'Failed to load team details', 'error')
      }
    }
  }

  return {
    packages_created: result.totalItems,
    teams_contacted: teamsContacted,
    last_activity: result.items[0]?.created || null,
  }
}
